"use client";

import { cva, type VariantProps } from "class-variance-authority";
import type { InputHTMLAttributes, ReactNode } from "react";
import { useRef } from "react";
import { cn } from "../../lib/utils";

const inputVariants = cva(
  "flex h-9 w-full rounded-md border bg-white px-3 py-1 text-sm outline-none transition-colors file:mr-3 file:cursor-pointer file:border-0 file:bg-transparent file:font-bold file:text-sm placeholder:text-zinc-900 focus:ring-2 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-zinc-200 shadow-sm focus:border-zinc-900 focus:ring-zinc-900 focus-visible:border-zinc-900 focus-visible:ring-zinc-900",
        error:
          "border-red-600 shadow-[0_0_0_3px_rgba(254,202,202,1)] focus:border-red-600 focus:ring-red-200 focus-visible:border-red-600 focus-visible:ring-red-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "className"
> &
  VariantProps<typeof inputVariants> & {
    className?: string;
  };

export function Input(props: InputProps): ReactNode {
  const { variant, className, ...restProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (props.type === "file") {
      inputRef.current?.focus();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.type === "file") {
      inputRef.current?.focus();
    }
    props.onChange?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    props.onBlur?.(event);
  };

  return (
    <input
      className={cn(inputVariants({ variant }), className)}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      ref={inputRef}
      {...restProps}
    />
  );
}
